<script lang="ts">
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import { createFormControlId, emptyFormFieldWatcher, errorMessagesForFormControl, FormControl, FormControlComponent, FormControlValue, FormFunctions, internalValuesChanged, isFieldShownAsContainingAnError, labelWithRequiredIndicator, mountFormControl, wasValidationSuccessful } from '@/components/form';
import { ingredientUnitTranslations } from '@/helpers/data';
import CreateIngredientDialog from './create-ingredient-dialog.vue';
import UpdateIngredientDialog from './update-ingredient-dialog.vue';
import { Ingredient } from '../types';

@Component({
  components: {
    CreateIngredientDialog,
    UpdateIngredientDialog,
  },
  methods: { labelWithRequiredIndicator, isFieldShownAsContainingAnError },
})
export default class IngredientsFormControl extends Vue implements FormControlComponent<Ingredient[]> {

  @Inject('formFunctions')
  readonly formFunctions!: FormFunctions;

  @Prop({ type: Object, required: true })
  readonly formControl!: FormControl<Ingredient[]>;

  readonly formControlId = createFormControlId();

  readonly ingredientUnitTranslations = ingredientUnitTranslations;

  isFocused = false;
  isTouched = false;

  messages: string[] = [];

  internalValue: Ingredient[] = [];

  formFieldValueWatcher = emptyFormFieldWatcher();

  mounted(): void {
    mountFormControl(this);
  }

  focused(): void {
    this.isFocused = true;
  }

  blurred(): void {
    this.isFocused = false;
    this.isTouched = true;
  }

  createItem(item: Ingredient): void {
    this.internalValue.push(item);

    this.blurred();

    internalValuesChanged(this);
  }

  updateItem(index: number, item: Ingredient): void {
    this.internalValue.splice(index, 1, item);

    this.blurred();

    internalValuesChanged(this);
  }

  deleteItem(index: number): void {
    this.internalValue.splice(index, 1);

    this.blurred();

    internalValuesChanged(this);
  }

  // -- Form control functions

  validateFormValue(): boolean {
    this.messages = [
      ...errorMessagesForFormControl(this.formControl),
    ];

    return wasValidationSuccessful(this.messages);
  }

  updateInternalValues(): void {
    this.internalValue = this.formControl.value === null
      ? []
      : this.formControl.value;
  }

  formValueFromInternalValues(): FormControlValue<Ingredient[]> {
    return this.internalValue.length > 0
      ? this.internalValue
      : null;
  }

}
</script>
<template>
<div class="form-control ingredients-form-control" v-bind="$attrs">
  <fieldset class="a-field pt-2 pb-4">
    <legend>{{ labelWithRequiredIndicator(formControl) }}</legend>
    <div class="form-control-input">
      <ul>
        <li
          v-for="(ingredient, index) in internalValue"
          :key="index"
        >
          <div class="d-flex">
            <div class="d-flex flex-grow-1">
              <template v-if="ingredient.unit === 'TO_TASTE'">
                {{ ingredient.ingredient }} nach Geschmack
              </template>
              <template v-else-if="ingredient.unit === 'PIECE'">
                {{ ingredient.amount }} {{ ingredient.ingredient }}
              </template>
              <template v-else>
                {{ ingredient.amount }} {{ ingredientUnitTranslations[ingredient.unit] }} {{ ingredient.ingredient }}
              </template>
            </div>
            <div class="actions flex-shrink-0 pl-2">
              <update-ingredient-dialog
                :ingredient="ingredient"
                @submitted="updateItem(index, $event)"
                @closed="blurred"
              >
                <icon-button
                  :icon="['far', 'pencil']"
                  tooltip="Anpassen"
                  small
                />
              </update-ingredient-dialog>
              <icon-button
                :icon="['far', 'times']"
                @click="deleteItem(index)"
                tooltip="Löschen"
                small
              />
            </div>
          </div>
        </li>
      </ul>
      <create-ingredient-dialog
        @submitted="createItem"
        @closed="blurred"
      >
        <a-execute-secondary-action-button
          @click="focused"
          is-small
          class="mt-2"
        >
          Zutat hinzufügen
        </a-execute-secondary-action-button>
      </create-ingredient-dialog>
    </div>
  </fieldset>

    <a-form-control-messages
      :messages="messages"
      :is-focussed="isFocused"
      :is-touched="isTouched"
    />
</div>
</template>
